/**
 * 本地传手机模式的座位与身份（§11.8）。
 *
 * 本地玩家是 `LocalPlayer { localId, name }`，与账号体系**没有任何映射**：
 * 红线 #11 明令禁止把本地席位绑定到真实 `accountId`，那会制造「一人操作多账号」的通道，
 * 而且对「几个人共用一部手机」这个场景毫无用处。
 *
 * 引擎的 `TableSeatSnapshot.accountId` 是必填的（null 表示空座），所以这里合成一个
 * `loc_` 前缀的假身份。它刻意**不是** `acc_` 前缀 —— `hasIdPrefix(id,'account')` 对它返回 false，
 * 任何误把本地对局当成账号对局写进统计/服务端的代码都会在类型之外多一道运行时特征可查。
 *
 * 座位数是动态的：2~8 人全部支持（`SEAT_LIMITS`），座位号恒为 0..n-1 连续占满，
 * `maxSeats` 就等于人数 —— 本地模式所有人都在场，没有空座的概念。
 */
import type { AccountId, Chips, SeatIndex, TableSeatSnapshot } from '@poker/shared'
import { SEAT_LIMITS } from '@poker/shared'

import type { SeatNote } from '../store/tableStore.js'

export interface LocalPlayer {
  /** 本机内唯一，换名字不换 id，手牌历史因此可以跨改名归并 */
  readonly localId: string
  readonly name: string
  readonly seat: SeatIndex
  readonly stack: Chips
  /** 筹码归零后置 true；引擎侧本来就会把 stack===0 的座位排除在发牌之外 */
  readonly sittingOut: boolean
}

/** 默认昵称池。够 8 个，长度都控制在 3 个汉字内 —— 座位块在 small 密度下只有 58px 宽。 */
export const DEFAULT_LOCAL_NAMES: readonly string[] = [
  '玩家一',
  '玩家二',
  '玩家三',
  '玩家四',
  '玩家五',
  '玩家六',
  '玩家七',
  '玩家八',
]

/** 昵称最长 8 字符：再长在 58px 的座位块里必然截断，不如在输入处就拦住。 */
export const MAX_NAME_LEN = 8

/** 合成身份的前缀。刻意不是 `acc_`（红线 #11）。 */
const LOCAL_ID_PREFIX = 'loc_'

export function localAccountId(localId: string): AccountId {
  return `${LOCAL_ID_PREFIX}${localId}`
}

export function isLocalAccountId(id: string): boolean {
  return id.startsWith(LOCAL_ID_PREFIX)
}

/** 把任意输入夹到 [MIN, MAX]；非整数/NaN 一律退回 MIN，不静默放行。 */
export function clampSeatCount(n: number): number {
  if (!Number.isFinite(n)) return SEAT_LIMITS.MIN
  const i = Math.round(n)
  if (i < SEAT_LIMITS.MIN) return SEAT_LIMITS.MIN
  if (i > SEAT_LIMITS.MAX) return SEAT_LIMITS.MAX
  return i
}

/** 可选人数列表，直接喂给开局页的人数选择器。 */
export function seatCountOptions(): number[] {
  const out: number[] = []
  for (let n = SEAT_LIMITS.MIN; n <= SEAT_LIMITS.MAX; n++) out.push(n)
  return out
}

/** 去掉首尾空白与换行；空串回落到默认名。昵称是要显示在遮牌页上的，不能是空白。 */
export function normalizeName(raw: string, fallback: string): string {
  const t = raw.replace(/\s+/gu, ' ').trim()
  if (t.length === 0) return fallback
  return t.length > MAX_NAME_LEN ? t.slice(0, MAX_NAME_LEN) : t
}

export function defaultNameFor(seat: SeatIndex): string {
  return DEFAULT_LOCAL_NAMES[seat] ?? `玩家${seat + 1}`
}

/**
 * 建 n 个本地玩家。`names` 缺项自动补默认名。
 *
 * localId 用 `seat` 而不是随机串：本地对局不跨设备同步，座位号已经唯一，
 * 随机串只会让「同一个人换座」这种将来的需求更难对齐。
 */
export function makeLocalPlayers(
  count: number,
  startingStack: Chips,
  names: readonly string[] = [],
): LocalPlayer[] {
  const n = clampSeatCount(count)
  const out: LocalPlayer[] = []
  for (let seat = 0; seat < n; seat++) {
    const fallback = defaultNameFor(seat)
    out.push({
      localId: `p${seat}`,
      name: normalizeName(names[seat] ?? fallback, fallback),
      seat,
      stack: startingStack,
      sittingOut: false,
    })
  }
  return out
}

/** 增减人数时保留已填的昵称与筹码，只补/裁末尾几位。 */
export function resizeLocalPlayers(
  players: readonly LocalPlayer[],
  count: number,
  startingStack: Chips,
): LocalPlayer[] {
  const n = clampSeatCount(count)
  if (n <= players.length) return players.slice(0, n)
  const out = players.slice()
  for (let seat = players.length; seat < n; seat++) {
    const fallback = defaultNameFor(seat)
    out.push({ localId: `p${seat}`, name: fallback, seat, stack: startingStack, sittingOut: false })
  }
  return out
}

export function renameLocalPlayer(
  players: readonly LocalPlayer[],
  seat: SeatIndex,
  name: string,
): LocalPlayer[] {
  return players.map((p) =>
    p.seat === seat ? { ...p, name: normalizeName(name, defaultNameFor(seat)) } : p,
  )
}

/** 座位 → 昵称。找不到时给个稳定的兜底，绝不返回 undefined（遮牌页靠它写「交给谁」）。 */
export function nameOfSeat(players: readonly LocalPlayer[], seat: SeatIndex | null): string {
  if (seat === null) return '全桌'
  return players.find((p) => p.seat === seat)?.name ?? defaultNameFor(seat)
}

/** 引擎输入。空座不存在：本地模式所有人都在场。 */
export function toSeatSnapshots(players: readonly LocalPlayer[]): TableSeatSnapshot[] {
  return players.map((p) => ({
    seat: p.seat,
    accountId: localAccountId(p.localId),
    stack: p.stack,
    sittingOut: p.sittingOut,
    // 本地模式没有「入局」的概念（等大盲 / 补盲）：人是一次性凑齐开局的，中途不会有人插进来。
    // 标成入局中会让人第一手拿不到牌，对着同一部手机的几个人只会觉得是 bug。
    entry: null,
  }))
}

/** 结算后把筹码写回；归零者自动 sitOut（引擎本来也不会给 stack===0 的座位发牌）。 */
export function applyFinalStacks(
  players: readonly LocalPlayer[],
  finalStacks: Readonly<Record<SeatIndex, Chips>>,
): LocalPlayer[] {
  return players.map((p) => {
    const stack = finalStacks[p.seat] ?? p.stack
    return { ...p, stack, sittingOut: stack <= 0 }
  })
}

/** 还能拿牌的人。少于 2 人就没法开下一手了。 */
export function dealablePlayers(players: readonly LocalPlayer[]): LocalPlayer[] {
  return players.filter((p) => !p.sittingOut && p.stack > 0)
}

export function canDealNextHand(players: readonly LocalPlayer[]): boolean {
  return dealablePlayers(players).length >= SEAT_LIMITS.MIN
}

/** 名次：筹码多的在前，同筹码按座位号。用于「游戏结束」的结算页。 */
export function standings(players: readonly LocalPlayer[]): LocalPlayer[] {
  return players.slice().sort((a, b) => (b.stack - a.stack) || (a.seat - b.seat))
}

/**
 * 本地座位的角标（与联机同一套词，见 store 的 `SeatNote`）。
 * 本地传手机只会因为输光而坐出，也没有网络可断；没有买分，所以不会是「买分中」。
 */
export function localSeatNote(s: { readonly connected: boolean; readonly sittingOut: boolean; readonly stack: Chips }): SeatNote | null {
  if (!s.connected) return 'offline'
  if (!s.sittingOut) return null
  return s.stack <= 0 ? 'broke' : 'away'
}
